import {LRUCache} from '@github-ui/relay-query-cache/lru-cache'
import {PERSISTER_KEY_PREFIX, generateCacheKey} from '@github-ui/relay-query-cache'
import type {createSafeIndexedDbPersister} from '@github-ui/safe-indexed-db-storage'
import {sendAnalyticsEvent} from './service-worker-analytics'
import {reportError as reportErrorToFailbot, type ServiceWorkerErrorContext} from './service-worker-failbot'
import {
  SERVICE_WORKER_VERSION,
  SERVICE_WORKER_EVENTS,
  SW_CACHE_MAX_SIZE,
  ISSUE_VIEWER_VIEW_QUERY_NAME,
  ISSUE_INDEX_PAGE_QUERY_NAME,
} from './sw-constants'

export type UrlIssueParams = {
  owner: string
  repo: string
  issueNumber: number
}

export type UrlIssueIndexParams = {
  owner: string
  repo: string
}

export type InterceptablePageParams =
  | ({type: 'issue-show'} & UrlIssueParams)
  | ({type: 'issue-index'} & UrlIssueIndexParams)

export const reportError = (error: unknown, context?: ServiceWorkerErrorContext): void => {
  reportErrorToFailbot(error, context)
  sendAnalyticsEvent(SERVICE_WORKER_EVENTS.ERROR, 'reportError', {
    error_message: error instanceof Error ? error.message : 'unknown',
    service_worker_version: SERVICE_WORKER_VERSION,
    request_id: context?.requestId,
    status_code: context?.statusCode,
    target_url: context?.targetUrl,
    referrer: context?.referrer,
    nav_type: context?.navType,
  })
}

export const swCache = new LRUCache(SW_CACHE_MAX_SIZE)

// Module-level state
let currentUser: string | null = null
let release: string | null = null
let actorId: string | null = null
let storage: ReturnType<typeof createSafeIndexedDbPersister> | null = null

export const getCurrentUser = (): string | null => currentUser
export const getRelease = (): string | null => release
export const getActorId = (): string | null => actorId
export const getStorage = (): ReturnType<typeof createSafeIndexedDbPersister> | null => storage

export const setCurrentUser = (user: string | null): void => {
  currentUser = user
}
export const setRelease = (r: string | null): void => {
  release = r
}
export const setActorId = (id: string | null): void => {
  actorId = id
}
export const setStorage = (s: ReturnType<typeof createSafeIndexedDbPersister> | null): void => {
  storage = s
}

// For testing purposes only
export const resetState = () => {
  currentUser = null
  release = null
  actorId = null
  storage = null
  swCache.clear()
}

export const generateIssueViewerViewQueryCacheKey = (
  {owner, repo, issueNumber}: UrlIssueParams,
  tabbedTimeline = false,
) => {
  const prefix = `${PERSISTER_KEY_PREFIX}-${currentUser}`
  const defaultValues = {
    count: 15,
    hideTimeline: false,
    tabbedTimeline: false,
  }
  const queryParameters = {
    queryId: '', // queryId is not needed for cache key generation
    queryName: ISSUE_VIEWER_VIEW_QUERY_NAME,
    variables: {
      owner,
      repo,
      number: issueNumber,
      tabbedTimeline,
      ...(tabbedTimeline ? {viewType: 'CONVERSATION'} : {}),
    },
  }
  return generateCacheKey(prefix, queryParameters, defaultValues)
}

export const getGitHubIssueParams = (url: string): UrlIssueParams | undefined => {
  try {
    const parsed = new URL(url, self.location.origin)
    const pathSegments = parsed.pathname.split('/').filter(Boolean)

    // Expected format: /{owner}/{repo}/issues/{number}
    // Reject URLs with extra path segments (e.g., /issues/123/unlock)
    if (pathSegments.length !== 4) return undefined
    if (pathSegments[2] !== 'issues') return undefined

    const issueNumber = pathSegments[3]
    // Validate issue number is digits only
    if (issueNumber !== undefined && /^\d+$/.test(issueNumber) && pathSegments[0] && pathSegments[1]) {
      return {
        owner: pathSegments[0],
        repo: pathSegments[1],
        issueNumber: parseInt(issueNumber, 10),
      }
    }
    return undefined
  } catch {
    return undefined
  }
}

export const getGitHubIssueIndexParams = (url: string): UrlIssueIndexParams | undefined => {
  try {
    const parsed = new URL(url, self.location.origin)
    const pathSegments = parsed.pathname.split('/').filter(Boolean)

    // Expected format: /{owner}/{repo}/issues
    if (pathSegments.length !== 3) return undefined
    if (pathSegments[2] !== 'issues') return undefined
    if (!pathSegments[0] || !pathSegments[1]) return undefined

    return {
      owner: pathSegments[0],
      repo: pathSegments[1],
    }
  } catch {
    return undefined
  }
}

export const parseInterceptableUrl = (url: string): InterceptablePageParams | undefined => {
  const issueParams = getGitHubIssueParams(url)
  if (issueParams) {
    return {type: 'issue-show', ...issueParams}
  }
  const indexParams = getGitHubIssueIndexParams(url)
  if (indexParams) {
    return {type: 'issue-index', ...indexParams}
  }
  return undefined
}

// Assuming here that issues_default_best_match_sort and issues_search_type_gql are fully enabled.
// `showIssueFieldPills` is gated by a feature flag at runtime; the cache key uses the query's
// default value (`false`) so the SW key matches the preheated key when the flag is off.
export const getIssueIndexPageQueryDefaultValues = ({owner, repo}: UrlIssueIndexParams) => ({
  query: `is:issue state:open repo:${owner}/${repo} sort:relevance-desc`,
  first: 25,
  labelPageSize: 20,
  showIssueFieldPills: false,
  skip: 0,
  type: 'ISSUE_HYBRID',
})

export const generateIssueIndexPageQueryCacheKey = ({owner, repo}: UrlIssueIndexParams) => {
  const prefix = `${PERSISTER_KEY_PREFIX}-${currentUser}`
  const queryParameters = {
    queryId: '',
    queryName: ISSUE_INDEX_PAGE_QUERY_NAME,
    variables: {owner, name: repo},
  }
  return generateCacheKey(prefix, queryParameters, getIssueIndexPageQueryDefaultValues({owner, repo}))
}

// Try both tabbedTimeline variants since the SW doesn't know the user's preference.
// Order: true first (direction of rollout), then false as fallback.
const TABBED_TIMELINE_VARIANTS: readonly boolean[] = [true, false]

export const generateCacheKeysForParams = (params: InterceptablePageParams): string[] => {
  switch (params.type) {
    case 'issue-show':
      return TABBED_TIMELINE_VARIANTS.map(tabbedTimeline =>
        generateIssueViewerViewQueryCacheKey(params, tabbedTimeline),
      )
    case 'issue-index':
      return [generateIssueIndexPageQueryCacheKey(params)]
  }
}

export const getQueryNameForParams = (params: InterceptablePageParams): string => {
  switch (params.type) {
    case 'issue-show':
      return ISSUE_VIEWER_VIEW_QUERY_NAME
    case 'issue-index':
      return ISSUE_INDEX_PAGE_QUERY_NAME
  }
}
